import React from 'react';
import { Shield, Heart, Zap, RefreshCw, Target, Users } from 'lucide-react';

const ApexProblemSolution = () => {
  const problems = [
    {
      id: 'problem-1',
      icon: Shield,
      title: 'Fear of Being "Salesy"',
      description: 'You got into fitness to help people, not to be a pushy salesperson. The thought of "selling" makes you uncomfortable.'
    },
    {
      id: 'problem-2',
      icon: RefreshCw,
      title: 'Handling Objections',
      description: 'You dread the moment a prospect says "I need to think about it" or "It is too expensive." You never know what to say.'
    },
    {
      id: 'problem-3',
      icon: Target,
      title: 'Inconsistent Results',
      description: 'Some months are great, others are terrible. You cannot predict your revenue or plan for growth.'
    }
  ];

  const solutions = [
    {
      id: 'solution-1',
      icon: Heart,
      title: 'Confident & Authentic',
      description: 'We teach you a process that feels natural — helping prospects see the value without any pressure. You will actually enjoy sales calls.'
    },
    {
      id: 'solution-2',
      icon: Zap,
      title: 'Zero Objections Process',
      description: 'When done right, prospects have zero objections by the time you present your offer. It\'s about the journey, not handling pushback.'
    },
    {
      id: 'solution-3',
      icon: Users,
      title: 'Predictable Growth',
      description: 'Improve every stage: booking rates, show rates, close rates, and retention. Build a business you can actually plan around.'
    }
  ];

  return (
    <section id="benefits" className="relative py-20 md:py-28 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="text-[#22C55E] text-sm font-medium tracking-[0.2em] uppercase">
            We Understand Your Struggle
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 max-w-3xl mx-auto">
            You Are Great at Transforming Bodies.
            <br />
            <span className="text-[#A1A1AA]">Let Us Transform Your Sales.</span>
          </h2>
        </div>

        {/* Problem / Solution Grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Problems Column */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-500/30 bg-red-500/10 mb-4">
              <span className="w-2 h-2 bg-red-500 rounded-full" />
              <span className="text-red-400 text-sm font-medium">The Problem</span>
            </div>
            
            <div className="space-y-4">
              {problems.map((problem) => (
                <div
                  key={problem.id}
                  data-testid={problem.id}
                  className="bg-[#121212] border border-[#27272A] rounded-xl p-6 hover:border-red-500/30 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                      <problem.icon className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{problem.title}</h3>
                      <p className="text-[#A1A1AA] text-sm">{problem.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Solutions Column */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#22C55E]/30 bg-[#22C55E]/10 mb-4">
              <span className="w-2 h-2 bg-[#22C55E] rounded-full" />
              <span className="text-[#22C55E] text-sm font-medium">Our Solution</span>
            </div>
            
            <div className="space-y-4">
              {solutions.map((solution) => (
                <div
                  key={solution.id}
                  data-testid={solution.id}
                  className="bg-[#121212] border border-[#27272A] rounded-xl p-6 hover:border-[#22C55E]/30 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#22C55E]/10 flex items-center justify-center flex-shrink-0">
                      <solution.icon className="w-5 h-5 text-[#22C55E]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{solution.title}</h3>
                      <p className="text-[#A1A1AA] text-sm">{solution.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom quote */}
        <div className="mt-16 text-center">
          <blockquote className="max-w-3xl mx-auto">
            <p className="text-xl md:text-2xl text-white italic leading-relaxed">
              &ldquo;You do not need to handle a million objections. You need to take clients through a process 
              that leaves them with <span className="text-[#22C55E] font-semibold">absolutely zero objections</span> by 
              the time you present your offer.&rdquo;
            </p>
          </blockquote>
        </div>
      </div>
    </section>
  );
};

export default ApexProblemSolution;
